import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { BarChart2, Download, Loader2 } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { PageHeader } from "../../../components/Page";
import Hamburger from "../../../components/Hamburger";
import { ReactComponent as GNAiIcon } from "../../../icons/gnai_logo.svg";
import { CgsTitle } from "../components";
import {
  deriveAnalytics,
  deriveReturnAnalytics,
  GRANULARITY_TO_GROUP_BY,
} from "./helpers";
import Tooltip from "../../../components/tooltip";
import { DetailTab, Granularity, PeriodRow, TopTab } from "./types";
import { ContractsSidebar, CUMULATIVE_ID } from "./components/ContractsSidebar";
import { DetailTitle } from "./components/DetailTitle";
import { DetailTabs } from "./components/DetailTabs";
import { OverviewTab } from "./tabs/OverviewTab";
import { DocumentsTab } from "./tabs/DocumentsTab";
import { CumulativeView } from "./tabs/CumulativeView";
import {
  getBankingCumulativeOverview,
  getBankingCumulativeReturn,
  getBankingCumulativeSupply,
  getBankingCumulativeSupplyDestinations,
  getBankingCumulativeSupplySources,
  getBankingContractAnalytics,
  getBankingContractReturnAnalytics,
  getBankingSummaryExport,
} from "../../../Rest_api/RestApi";
import {
  BankingContractAnalytics,
  BankingContractAnalyticsBucket,
  BankingContractReturnAnalytics,
  BankingCumulativeOverview,
  BankingCumulativeReturnAnalytics,
  BankingCumulativeSupplyAnalytics,
  BankingCumulativeSupplyDestinations,
  BankingCumulativeSupplySources,
} from "../../../models/banking";

type ContractExcelHeader = {
  loaNumber: string;
  traderName: string;
  supplier: string;
  receiver: string;
};

function bucketToRow(
  d: BankingContractAnalyticsBucket,
  isBlock: boolean,
  header: ContractExcelHeader,
) {
  return isBlock
    ? {
        "LOI NO": header.loaNumber,
        TRADER: header.traderName,
        SELLER: header.supplier,
        BUYER: header.receiver,
        Categories: (d.categories ?? []).join(", "),
        "Approval Nos": (d.approval_nos ?? []).join(", "),
        Date: d.date,
        "Time Block": d.time_block ?? "",
        Time: d.time ?? "",
        "Scheduled MW": +d.scheduled_mwh.toFixed(2),
        "Actual MW": +d.actual_mwh.toFixed(2),
        "Delta MW": +d.delta_mwh.toFixed(2),
        "Delta %": +d.delta_pct.toFixed(1),
      }
    : {
        "LOI NO": header.loaNumber,
        TRADER: header.traderName,
        SELLER: header.supplier,
        BUYER: header.receiver,
        Categories: (d.categories ?? []).join(", "),
        "Approval Nos": (d.approval_nos ?? []).join(", "),
        Date: d.date,
        "Scheduled MWh": +d.scheduled_mwh.toFixed(2),
        "Actual MWh": +d.actual_mwh.toFixed(2),
        "Delta MWh": +d.delta_mwh.toFixed(2),
        "Delta %": +d.delta_pct.toFixed(1),
      };
}

function periodRowToExcel(
  r: PeriodRow,
  granularity: Granularity,
  header: ContractExcelHeader,
) {
  if (granularity === "Time Block") {
    return {
      "LOI NO": header.loaNumber,
      TRADER: header.traderName,
      SELLER: header.supplier,
      BUYER: header.receiver,
      Categories: (r.categories ?? []).join(", "),
      "Approval Nos": (r.approval_nos ?? []).join(", "),
      Date: r.date,
      Block: r.label,
      "Scheduled MW": +r.scheduled.toFixed(2),
      "Actual MW": +r.actual.toFixed(2),
      "Delta MW": +r.delta.toFixed(2),
      "Delta %": +r.pct.toFixed(1),
    };
  }
  return {
    "LOI NO": header.loaNumber,
    TRADER: header.traderName,
    SELLER: header.supplier,
    BUYER: header.receiver,
    Categories: (r.categories ?? []).join(", "),
    "Approval Nos": (r.approval_nos ?? []).join(", "),
    Date: r.label || r.date,
    "Scheduled MWh": +r.scheduled.toFixed(2),
    "Actual MWh": +r.actual.toFixed(2),
    "Delta MWh": +r.delta.toFixed(2),
    "Delta %": +r.pct.toFixed(1),
  };
}

function styleHeaderRow(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws["!ref"] ?? "A1");
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: range.s.r, c: col });
    const cell = ws[cellRef];
    if (!cell) continue;
    cell.s = {
      fill: { fgColor: { rgb: "4472C4" } },
      font: { color: { rgb: "FFFFFF" }, bold: true },
      alignment: { vertical: "center", horizontal: "center", wrapText: true },
    };
  }

  ws["!rows"] = [{ hpt: 28 }];

  const colCount = range.e.c - range.s.c + 1;
  ws["!cols"] = Array.from({ length: colCount }, (_, col) => {
    let maxLen = 10;
    for (let row = range.s.r; row <= range.e.r; row++) {
      const cell = ws[XLSX.utils.encode_cell({ r: row, c: col })];
      const len = cell?.v != null ? String(cell.v).length : 0;
      if (len > maxLen) maxLen = len;
    }
    return { wch: Math.min(maxLen + 2, 40) };
  });
}

function downloadWorkbook(wb: XLSX.WorkBook, filename: string) {
  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(
    new Blob([wbout], { type: "application/octet-stream" }),
    filename.replace(/[^a-zA-Z0-9_\-.]/g, "_"),
  );
}

export function BankingCalculator() {
  const [topTab, setTopTab] = useState<TopTab>("Active");
  const [detailTab, setDetailTab] = useState<DetailTab>("Overview");
  const [granularity, setGranularity] = useState<Granularity>("Day");
  const [selectedId, setSelectedId] = useState<string>(CUMULATIVE_ID);
  const [search, setSearch] = useState("");
  // Bumped whenever a contract is edited in the Documents tab so the analytics
  // effect re-fetches and the Overview chart reflects the updated schedule.
  const [contractRefreshKey, setContractRefreshKey] = useState(0);

  const [analyticsData, setAnalyticsData] = useState<{
    supply: BankingContractAnalytics | null;
    ret: BankingContractReturnAnalytics | null;
    granularity: Granularity | null;
  }>({ supply: null, ret: null, granularity: null });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  const [cumulativeOverview, setCumulativeOverview] =
    useState<BankingCumulativeOverview | null>(null);
  const [cumulativeSupply, setCumulativeSupply] =
    useState<BankingCumulativeSupplyAnalytics | null>(null);
  const [cumulativeReturn, setCumulativeReturn] =
    useState<BankingCumulativeReturnAnalytics | null>(null);
  const [cumulativeSources, setCumulativeSources] =
    useState<BankingCumulativeSupplySources | null>(null);
  const [cumulativeDestinations, setCumulativeDestinations] =
    useState<BankingCumulativeSupplyDestinations | null>(null);
  const [cumulativeLoading, setCumulativeLoading] = useState(false);
  const [cumulativeError, setCumulativeError] = useState<string | null>(null);

  const [exporting, setExporting] = useState(false);

  const isContractSelected = selectedId !== "" && selectedId !== CUMULATIVE_ID;

  useEffect(() => {
    if (!isContractSelected) {
      setAnalyticsData({ supply: null, ret: null, granularity: null });
      setAnalyticsError(null);
      setAnalyticsLoading(false);
      return;
    }
    // Only (re)fetch while the Overview tab is showing. Switching to Documents
    // must NOT reset analyticsData — the whole detail block (Documents
    // included) is gated on `derived`, so a null reset would hide that tab.
    // Arriving on Overview re-runs this effect and pulls fresh chart data.
    if (detailTab !== "Overview") return;
    let cancelled = false;
    setAnalyticsData({ supply: null, ret: null, granularity: null });
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    const groupBy = GRANULARITY_TO_GROUP_BY[granularity];
    Promise.all([
      getBankingContractAnalytics(selectedId, groupBy),
      getBankingContractReturnAnalytics(selectedId, groupBy).catch(() => null),
    ])
      .then(([supply, ret]) => {
        if (cancelled) return;
        if (!supply) {
          setAnalyticsData({ supply: null, ret: null, granularity: null });
          setAnalyticsError("No analytics available for this contract.");
          return;
        }
        setAnalyticsData({
          supply,
          ret,
          granularity,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setAnalyticsError(err?.message ?? "Failed to load analytics");
        setAnalyticsData({ supply: null, ret: null, granularity: null });
      })
      .finally(() => {
        if (!cancelled) setAnalyticsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    selectedId,
    isContractSelected,
    granularity,
    contractRefreshKey,
    detailTab,
  ]);

  // Static cumulative data — only re-fetches when contract selection changes
  useEffect(() => {
    if (selectedId !== CUMULATIVE_ID) {
      setCumulativeOverview(null);
      setCumulativeSources(null);
      setCumulativeDestinations(null);
      return;
    }
    let cancelled = false;
    setCumulativeOverview(null);
    setCumulativeSources(null);
    setCumulativeDestinations(null);
    Promise.all([
      getBankingCumulativeOverview().catch(() => null),
      getBankingCumulativeSupplySources().catch(() => null),
      getBankingCumulativeSupplyDestinations().catch(() => null),
    ])
      .then(([overview, sources, destinations]) => {
        if (cancelled) return;
        setCumulativeOverview(overview);
        setCumulativeSources(sources);
        setCumulativeDestinations(destinations);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  // Granularity-dependent cumulative data — re-fetches when group_by changes
  useEffect(() => {
    if (selectedId !== CUMULATIVE_ID) {
      setCumulativeSupply(null);
      setCumulativeReturn(null);
      setCumulativeError(null);
      setCumulativeLoading(false);
      return;
    }
    let cancelled = false;
    setCumulativeSupply(null);
    setCumulativeReturn(null);
    setCumulativeLoading(true);
    setCumulativeError(null);
    const groupBy = GRANULARITY_TO_GROUP_BY[granularity];
    Promise.all([
      getBankingCumulativeSupply(groupBy),
      getBankingCumulativeReturn(groupBy).catch(() => null),
    ])
      .then(([supply, ret]) => {
        if (cancelled) return;
        if (!supply) {
          setCumulativeError("No cumulative data available.");
          return;
        }
        setCumulativeSupply(supply);
        setCumulativeReturn(ret);
      })
      .catch((err) => {
        if (cancelled) return;
        setCumulativeError(
          err?.message ?? "Failed to load cumulative analytics",
        );
        setCumulativeSupply(null);
        setCumulativeReturn(null);
      })
      .finally(() => {
        if (!cancelled) setCumulativeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, granularity]);

  useEffect(() => {
    // Land on Overview for every newly selected contract so its analytics
    // fetch fires immediately (the analytics effect only fetches on Overview).
    setDetailTab("Overview");
    if (selectedId === CUMULATIVE_ID) setGranularity("Time Block");
  }, [selectedId]);

  const derived = useMemo(
    () =>
      analyticsData.supply && analyticsData.granularity !== null
        ? deriveAnalytics(analyticsData.supply, granularity)
        : null,
    [analyticsData, granularity],
  );

  const returnDerived = useMemo(
    () =>
      analyticsData.ret && analyticsData.granularity !== null
        ? deriveReturnAnalytics(analyticsData.ret, granularity)
        : null,
    [analyticsData, granularity],
  );

  // Export for individual contract — uses already-loaded data for current granularity
  const handleExport = useCallback(() => {
    if (!analyticsData.supply) return;
    setExporting(true);
    // Defer the (synchronous, CPU-heavy) workbook build to the next macrotask
    // so React can paint the "Exporting…" state before the main thread blocks.
    setTimeout(() => {
      try {
        const isTimeBlock = granularity === "Time Block";
        const wb = XLSX.utils.book_new();
        const contract = analyticsData.supply!.contract;
        const header: ContractExcelHeader = {
          loaNumber: contract.loa_number ?? "",
          traderName: contract.traderName ?? contract.trader ?? "",
          supplier: contract.supplier ?? "",
          receiver: contract.receiver ?? "",
        };

        // Supply sheet
        XLSX.utils.book_append_sheet(
          wb,
          (() => {
            const ws = XLSX.utils.json_to_sheet(
              isTimeBlock
                ? analyticsData.supply!.daily.map((d) =>
                    bucketToRow(d, true, header),
                  )
                : derived?.rows.map((r) =>
                    periodRowToExcel(r, granularity, header),
                  ) || [],
            );
            styleHeaderRow(ws);
            return ws;
          })(),
          `Supply (${granularity})`,
        );

        // Return sheet
        if (analyticsData.ret && analyticsData.ret.daily.length > 0) {
          XLSX.utils.book_append_sheet(
            wb,
            (() => {
              const ws = XLSX.utils.json_to_sheet(
                isTimeBlock
                  ? analyticsData.ret!.daily.map((d) =>
                      bucketToRow(d, true, header),
                    )
                  : returnDerived?.rows.map((r) =>
                      periodRowToExcel(r, granularity, header),
                    ) || [],
              );
              styleHeaderRow(ws);
              return ws;
            })(),
            `Return (${granularity})`,
          );
        }

        downloadWorkbook(
          wb,
          `banking_${analyticsData.supply!.contract.supplier}_${analyticsData.supply!.contract.receiver}_${granularity}.xlsx`,
        );
      } finally {
        setExporting(false);
      }
    }, 0);
  }, [analyticsData, derived, returnDerived, granularity]);

  // Export for cumulative view — downloads the server-generated Export | Import ledger
  const handleCumulativeExport = useCallback(async () => {
    setExporting(true);
    try {
      const blob = await getBankingSummaryExport("xlsx");
      saveAs(blob, "banking_summary.xlsx");
    } catch (err) {
      console.error("Failed to download banking summary", err);
    } finally {
      setExporting(false);
    }
  }, []);

  const leftHeader = (
    <div className="flex items-center gap-5">
      <GNAiIcon width={80} />
      <Hamburger />
      <h1 className="text-[18px] text-[#6F6D7C]">Banking Calculator</h1>
    </div>
  );

  return (
    <div className="page-wrapper">
      <PageHeader leftComponent={leftHeader} middleComponent={<CgsTitle />} />

      <div className="tabContent">
        <div className="flex flex-row w-full bg-[#F7F8FB] min-h-[calc(100vh-80px)] mt-3">
          <ContractsSidebar
            topTab={topTab}
            setTopTab={setTopTab}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            search={search}
            setSearch={setSearch}
          />

          <section className="flex-1 px-3 pb-8 overflow-y-auto">
            {selectedId === CUMULATIVE_ID &&
              cumulativeLoading &&
              !cumulativeSupply && (
                <div className="flex flex-col items-center justify-center min-h-[800px] bg-white/50 backdrop-blur-sm rounded-[12px] border border-[#ECEEF3] shadow-sm animate-in fade-in duration-500">
                  <div className="relative flex items-center justify-center">
                    <div className="absolute w-12 h-12 border-2 border-[#E2682B]/10 rounded-full" />
                    <Loader2 className="w-8 h-8 text-[#E2682B] animate-spin" />
                  </div>
                  <div className="mt-4 flex flex-col items-center">
                    <span className="text-[14px] font-semibold text-[#E2682B]">
                      Aggregating Network Data
                    </span>
                    <span className="text-[12px] text-[#828A99] mt-1">
                      Combining and processing all active contracts...
                    </span>
                  </div>
                </div>
              )}

            {selectedId === CUMULATIVE_ID &&
              cumulativeError &&
              !cumulativeLoading && (
                <div className="bg-white rounded-md p-6 text-[13px] text-[#E0463C]">
                  {cumulativeError}
                </div>
              )}

            {selectedId === CUMULATIVE_ID && cumulativeSupply && (
              <>
                <div className="flex flex-col gap-1.5 bg-white p-5 rounded-s-md rounded-e-md">
                  <div className="flex items-start justify-between gap-3">
                    <h1 className="text-[20px] font-bold text-[#525062]">
                      All contracts
                    </h1>
                    <div className="flex gap-2 pt-1">
                      <button
                        type="button"
                        onClick={handleCumulativeExport}
                        disabled={exporting}
                        className="inline-flex items-center gap-1.5 border border-[#ECEEF3] bg-white rounded-lg px-3 py-1.5 text-xs text-[#525062] font-medium hover:border-[#DADCE3] hover:bg-[#FAFBFD] disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {exporting ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Download size={14} />
                        )}
                        {exporting ? "Exporting…" : "Download report"}
                      </button>
                    </div>
                  </div>
                  {cumulativeOverview && (
                    <div className="text-xs text-[#6B6877] flex flex-wrap items-center gap-1">
                      {[
                        {
                          label: "Contracts combined",
                          value: String(cumulativeOverview.contracts_combined),
                        },
                        {
                          label: "Avg banking factor",
                          value: `${cumulativeOverview.avg_banking_factor_pct.toFixed(1)}%`,
                        },
                        {
                          label: "Drawing supply",
                          value: String(cumulativeOverview.drawing_supply),
                        },
                        {
                          label: "Returning power",
                          value: String(cumulativeOverview.returning_power),
                        },
                        {
                          label: "Giving supply",
                          value: String(cumulativeOverview.giving_supply),
                        },
                        {
                          label: "Receiving back",
                          value: String(cumulativeOverview.receiving_back),
                        },
                        {
                          label: "Net variance",
                          value: `${cumulativeOverview.net_variance_pct >= 0 ? "+" : ""}${cumulativeOverview.net_variance_pct.toFixed(2)}%`,
                          neg: cumulativeOverview.net_variance_pct < 0,
                        },
                      ].map((item, i, arr) => (
                        <Fragment key={item.label}>
                          <span className="flex gap-2">
                            {item.label}{" "}
                            <strong
                              className={
                                "neg" in item && item.neg
                                  ? "text-[#E0463C] font-medium"
                                  : "text-[#4A5160] font-medium"
                              }
                            >
                              {item.value}
                            </strong>
                          </span>
                          {i < arr.length - 1 && (
                            <span className="text-[#828A99] mx-1">·</span>
                          )}
                        </Fragment>
                      ))}
                    </div>
                  )}
                </div>
                <CumulativeView
                  supplyAnalytics={cumulativeSupply}
                  returnAnalytics={cumulativeReturn}
                  supplySources={cumulativeSources}
                  supplyDestinations={cumulativeDestinations}
                  granularity={granularity}
                  setGranularity={setGranularity}
                />
              </>
            )}

            {isContractSelected && analyticsLoading && !derived && (
              <div className="flex flex-col items-center justify-center min-h-[800px] bg-white/50 backdrop-blur-sm rounded-[12px] border border-[#ECEEF3] shadow-sm animate-in fade-in duration-500">
                <div className="relative flex items-center justify-center">
                  <div className="absolute w-12 h-12 border-2 border-[#E2682B]/10 rounded-full" />
                  <Loader2 className="w-8 h-8 text-[#E2682B] animate-spin" />
                </div>
                <div className="mt-4 flex flex-col items-center">
                  <span className="text-[14px] font-semibold text-[#E2682B]">
                    Preparing Analytics
                  </span>
                  <span className="text-[12px] text-[#828A99] mt-1">
                    Fetching latest high-resolution banking data...
                  </span>
                </div>
              </div>
            )}

            {isContractSelected && analyticsError && !analyticsLoading && (
              <div className="flex flex-col items-center justify-center min-h-[800px] bg-white/50 backdrop-blur-sm rounded-[12px] border border-[#ECEEF3] shadow-sm animate-in fade-in duration-500">
                <div className="relative flex items-center justify-center mb-4">
                  <div className="absolute w-16 h-16 rounded-full bg-[#F5F6F8]" />
                  <div className="relative w-14 h-14 rounded-full bg-[#F0F1F5] flex items-center justify-center">
                    <BarChart2 className="w-6 h-6 text-[#E2682B]" />
                  </div>
                </div>
                <div className="flex flex-col items-center gap-1.5">
                  <span className="text-[15px] font-semibold text-[#E2682B]">
                    No Analytics Available
                  </span>
                  <span className="text-[12px] text-[#828A99] max-w-[260px] text-center leading-relaxed">
                    {analyticsError}
                  </span>
                </div>
              </div>
            )}

            {isContractSelected && derived && (
              <>
                <DetailTitle
                  contract={derived.contract}
                  onExport={handleExport}
                  exporting={exporting}
                />
                <DetailTabs detailTab={detailTab} setDetailTab={setDetailTab} />

                {detailTab === "Overview" && (
                  <OverviewTab
                    key={selectedId}
                    contract={derived.contract}
                    totals={derived.totals}
                    returnRequired={derived.returnRequired}
                    avgReturn={derived.avgReturn}
                    series={derived.series}
                    granularity={granularity}
                    setGranularity={setGranularity}
                    rows={derived.rows}
                    returnDerived={returnDerived}
                    blocksByDate={derived.blocksByDate}
                  />
                )}
                {detailTab === "Documents" && (
                  <DocumentsTab
                    contractId={selectedId}
                    onContractUpdated={() =>
                      setContractRefreshKey((k) => k + 1)
                    }
                  />
                )}
              </>
            )}

            {!isContractSelected && selectedId !== CUMULATIVE_ID && (
              <div className="bg-white rounded-md p-6 text-[13px] text-[#6B6877]">
                Select a contract from the sidebar to view its analytics.
              </div>
            )}
            <div className="gna-footer-container">
              <div className="grid grid-cols-3 justify-between gap-5 ">
                <Tooltip
                  note={<p>Banking</p>}
                  text=" 
            This calculator aggregates data from all active banking contracts to provide insights into scheduled vs actual supply, returns, and variances. Use the tabs to explore detailed analytics, documents, and cumulative views across contracts."
                >
                  <p className="gna-footer-text">Source</p>
                </Tooltip>
                <p className="gna-footer-text col-span-1 text-center">
                  Powered by <strong>GNA Energy</strong>
                </p>
                <strong className="gna-footer-text col-span-1 text-end">
                  Beta Version
                </strong>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default BankingCalculator;
